import { getDocument } from '@atlaskit/browser-apis';

const CHUNK_LOAD_ERROR_MESSAGES = [
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	'Cannot find module',
];

type PerformanceResourceTimingWithResponseStatus = PerformanceResourceTiming & {
	responseStatus?: number;
};

const getFailedResourceUrl = (message: string): string | undefined => {
	return message.match(/https?:\/\/[^\s]+/)?.[0];
};

const getFailedResourceName = (url: string): string | undefined => {
	try {
		return new URL(url).pathname.split('/').filter(Boolean).at(-1);
	} catch {
		return undefined;
	}
};

const getResourceTiming = (
	url: string,
): PerformanceResourceTimingWithResponseStatus | undefined => {
	if (typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') {
		return undefined;
	}

	return performance
		.getEntriesByName(url, 'resource')
		.filter((entry): entry is PerformanceResourceTiming => entry.entryType === 'resource')
		.at(-1);
};

/**
 * Builds a small, URL-safe diagnostic payload for Forge renderer failures.
 * The full error message and resource URL are intentionally excluded because
 * Forge asset URLs can contain signed context tokens.
 */
export const getErrorDiagnostics = (error: Error): Record<string, unknown> => {
	const failedResourceUrl = getFailedResourceUrl(error.message);
	const failedResourceName = failedResourceUrl
		? getFailedResourceName(failedResourceUrl)
		: undefined;
	const resourceTiming = failedResourceUrl ? getResourceTiming(failedResourceUrl) : undefined;
	const responseStatus = resourceTiming?.responseStatus;
	const document = getDocument();

	return {
		errorCategory:
			error.name === 'ChunkLoadError' ||
			CHUNK_LOAD_ERROR_MESSAGES.some((message) => error.message.includes(message))
				? 'chunk-load'
				: 'runtime-error',
		...(failedResourceName ? { failedResourceName } : {}),
		...(typeof navigator !== 'undefined' ? { browserOnline: navigator.onLine } : {}),
		...(document ? { documentVisibilityState: document.visibilityState } : {}),
		...(resourceTiming
			? {
					resourceDuration: Math.round(resourceTiming.duration),
					resourceTransferSize: resourceTiming.transferSize,
					resourceEncodedBodySize: resourceTiming.encodedBodySize,
					resourceDecodedBodySize: resourceTiming.decodedBodySize,
					resourceProtocol: resourceTiming.nextHopProtocol,
				}
			: {}),
		...(typeof responseStatus === 'number' && responseStatus > 0
			? { resourceResponseStatus: responseStatus }
			: {}),
	};
};
